"use client";

import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel } from "swiper/modules";

import useNavigation from "./use-navigation";

import { cn } from "@/lib/utils";

function NavItem({ target, children, isActive }) {
  const handleNavClick = (target) => (e) => {
    e.preventDefault(); // Prevent default anchor behavior

    const element = document.querySelector(target);
    if (element) {
      window.scrollTo({
        behavior: "smooth",
        top: element.getBoundingClientRect().top + window.scrollY - 68,
      });
    } else {
      console.warn(`Element with target "${target}" not found`);
    }
  };

  return (
    <span
      className={cn(
        "relative inline-block pt-3.25 pb-3.75 overflow-hidden",
        "cursor-pointer text-grayscale-label whitespace-nowrap",
        "after:absolute after:bottom-0 after:left-0",
        "after:h-0.75 after:w-full after:rounded-t-lg",
        "after:bg-primary after:translate-y-full",
        "after:transition-transform after:duration-500",
        isActive && "text-grayscale-header after:translate-y-0"
      )}
      onClick={handleNavClick(target)}
    >
      {children}
    </span>
  );
}

const navItems = [
  {
    label: "Offline Centres",
    target: "#offline-centres",
  },
  {
    label: "Course Highlights",
    target: "#course-highlights",
  },
  {
    label: "Packages",
    target: "#course-packages",
  },
  {
    label: "Overview",
    target: "#course-overview",
  },
  {
    label: "Our Teachers",
    target: "#meet-our-teachers",
  },
  {
    label: "Our Mentors",
    target: "#meet-our-mentor",
  },
  {
    label: "Demo Videos",
    target: "#demo-videos",
  },
  {
    label: "What’s Included",
    target: "#included-courses",
  },
  {
    label: "Course Content",
    target: "#course-content",
  },
  {
    label: "Course FAQs",
    target: "#course-faqs",
  },
];

function CourseNavigation({ className }) {
  const navRef = React.useRef(null);
  const navSwiperRef = React.useRef(null);

  const { finalNavItems, currentVisibleItem } = useNavigation(
    navItems,
    "#course-sections",
    100
  );

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        navSwiperRef.current.classList.toggle(
          "lg:max-w-155",
          e.intersectionRatio >= 1
        );

        navRef.current.classList.toggle("z-30", e.intersectionRatio < 1);
      },
      { threshold: [1] }
    );

    observer.observe(navRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <nav
      ref={navRef}
      className={cn(
        "lg:sticky -top-px bg-primary-foreground border-b border-grayscale-input z-10",
        "overflow-auto scrollbar-hide",
        className
      )}
    >
      <div className="container-studyiq">
        <Swiper
          ref={navSwiperRef}
          className="!ml-0 !h-13 lg:max-w-155 [&_.swiper-slide]:!w-auto"
          modules={[FreeMode, Mousewheel]}
          spaceBetween={28}
          slidesPerView="auto"
          mousewheel={{ forceToAxis: true }}
          freeMode
        >
          {finalNavItems
            .filter((item) => item.isActive)
            .map((item) => (
              <SwiperSlide key={item.target}>
                <NavItem
                  target={item.target}
                  isActive={item.target === currentVisibleItem?.target}
                >
                  {item.label}
                </NavItem>
              </SwiperSlide>
            ))}
        </Swiper>
      </div>
    </nav>
  );
}

export default CourseNavigation;
